import React, { useEffect, useMemo, useState } from 'react';
import { 
  Calendar, 
  TrendingUp, 
  DollarSign, 
  ShoppingBag, 
  Award, 
  Plus, 
  ChevronRight,
  BarChart2,
  ArrowUpRight,
  CalendarDays,
  X,
  Check,
  Trash2
} from 'lucide-react';

const PeriodAnalysis = () => {
  const [hoveredPeriod, setHoveredPeriod] = useState(null);
  const [hoveredMonth, setHoveredMonth] = useState(null);

  // Real monthly data (derived from daily)
  const [monthlyDataLoading, setMonthlyDataLoading] = useState(false);
  const [monthlyDataError, setMonthlyDataError] = useState(null);
  const [monthlyDataSource, setMonthlyDataSource] = useState('Daily → Monthly');
  const ANALYSIS_YEAR = 2026;

  // Daily dataset (single source of truth for Monthly + Period tables)
  const [dailyPerfRows, setDailyPerfRows] = useState([]); // [{ day: 'YYYY-MM-DD', spend, revenue, orders }]
  const [dailyPerfLoading, setDailyPerfLoading] = useState(false);
  const [dailyPerfError, setDailyPerfError] = useState(null);
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newPeriod, setNewPeriod] = useState({
    name: '',
    startDate: `${ANALYSIS_YEAR}-01-01`,
    endDate: `${ANALYSIS_YEAR}-01-07`,
    color: 'bg-indigo-500', 
    lightColor: 'bg-indigo-50',
    textColor: 'text-indigo-600'
  });

  // Persisted periods loading
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [periodsError, setPeriodsError] = useState(null);

  // Delete period modal
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name, startDate, endDate }
  const [isDeleting, setIsDeleting] = useState(false);

  const colorOptions = [
    { name: 'Rose', bg: 'bg-rose-500', light: 'bg-rose-50', text: 'text-rose-600' },
    { name: 'Emerald', bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-600' },
    { name: 'Amber', bg: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-600' },
    { name: 'Indigo', bg: 'bg-indigo-500', light: 'bg-indigo-50', text: 'text-indigo-600' },
    { name: 'Blue', bg: 'bg-blue-500', light: 'bg-blue-50', text: 'text-blue-600' },
    { name: 'Purple', bg: 'bg-purple-500', light: 'bg-purple-50', text: 'text-purple-600' },
    { name: 'Slate', bg: 'bg-slate-800', light: 'bg-slate-100', text: 'text-slate-800' },
  ];

  // Start empty: user-defined periods only (no seeded/demo periods)
  const [periods, setPeriods] = useState([]);

  // Load saved periods from DB
  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        setPeriodsLoading(true);
        setPeriodsError(null);
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) {
          setPeriods([]);
          setPeriodsError('No brand selected');
          return;
        }
        const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
        const res = await fetch(`${base}/api/campaign-periods`, {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId }
        });
        const js = await res.json().catch(() => ({}));
        const rows = (res.ok && js?.ok && Array.isArray(js.rows)) ? js.rows : [];
        const mapped = rows.map((r) => {
          const startDate = String(r.start_date || '').slice(0, 10);
          const endDate = String(r.end_date || '').slice(0, 10);
          // map stored color (bg-*) back to palette (also supports raw strings)
          const color = r.color || 'bg-indigo-500';
          const opt = colorOptions.find(o => o.bg === color);
          return {
            id: r.id,
            name: r.name || 'Untitled',
            shortName: (r.name || 'Period').substring(0, 8),
            startDate,
            endDate,
            color,
            lightColor: opt?.light || 'bg-indigo-50',
            textColor: opt?.text || 'text-indigo-600',
            // computed later from daily dataset
            metrics: { spend: 0, revenue: 0, orders: 0, roas: 0, topProduct: '-' }
          };
        });
        setPeriods(mapped);
      } catch (e) {
        setPeriodsError(e?.message || 'Failed to load periods');
        setPeriods([]);
      } finally {
        setPeriodsLoading(false);
      }
    };
    fetchPeriods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [monthlyData, setMonthlyData] = useState([
    { id: 'jan', name: 'Jan', shortName: 'Jan', metrics: { spend: 0, revenue: 0, roas: 0, orders: 0, topProduct: '-' } },
    { id: 'feb', name: 'Feb', shortName: 'Feb', metrics: { spend: 0, revenue: 0, roas: 0, orders: 0, topProduct: '-' } },
    { id: 'mar', name: 'Mar', shortName: 'Mar', metrics: { spend: 0, revenue: 0, roas: 0, orders: 0, topProduct: '-' } },
    { id: 'apr', name: 'Apr', shortName: 'Apr', metrics: { spend: 0, revenue: 0, roas: 0, orders: 0, topProduct: '-' } },
    { id: 'may', name: 'May', shortName: 'May', metrics: { spend: 0, revenue: 0, roas: 0, orders: 0, topProduct: '-' } },
    { id: 'jun', name: 'Jun', shortName: 'Jun', metrics: { spend: 0, revenue: 0, roas: 0, orders: 0, topProduct: '-' } },
    { id: 'jul', name: 'Jul', shortName: 'Jul', metrics: { spend: 0, revenue: 0, roas: 0, orders: 0, topProduct: '-' } },
    { id: 'aug', name: 'Aug', shortName: 'Aug', metrics: { spend: 0, revenue: 0, roas: 0, orders: 0, topProduct: '-' } },
    { id: 'sep', name: 'Sep', shortName: 'Sep', metrics: { spend: 0, revenue: 0, roas: 0, orders: 0, topProduct: '-' } },
    { id: 'oct', name: 'Oct', shortName: 'Oct', metrics: { spend: 0, revenue: 0, roas: 0, orders: 0, topProduct: '-' } },
    { id: 'nov', name: 'Nov', shortName: 'Nov', metrics: { spend: 0, revenue: 0, roas: 0, orders: 0, topProduct: '-' } },
    { id: 'dec', name: 'Dec', shortName: 'Dec', metrics: { spend: 0, revenue: 0, roas: 0, orders: 0, topProduct: '-' } },
  ]);

  useEffect(() => {
    const fetchDailyDataset = async () => {
      try {
        setDailyPerfLoading(true);
        setDailyPerfError(null);
        setMonthlyDataLoading(true);
        setMonthlyDataError(null);
        setMonthlyDataSource('Loading…');

        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) {
          setDailyPerfError('No brand selected');
          setMonthlyDataError('No brand selected');
          return;
        }

        const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
        const startDate = `${ANALYSIS_YEAR}-01-01`;
        const endDate = `${ANALYSIS_YEAR}-12-31`;

        const params = new URLSearchParams({ startDate, endDate });
        const res = await fetch(`${base}/api/performance/daily?${params.toString()}`, {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId }
        });
        const js = await res.json().catch(() => ({}));
        const rows = (res.ok && js?.ok && Array.isArray(js.rows)) ? js.rows : [];

        const dailyRows = rows.map(r => ({
          day: String(r.day || '').slice(0, 10),
          spend: Number(r.spend || 0),
          revenue: Number(r.revenue || 0),
          orders: Number(r.orders || 0),
        }));

        setDailyPerfRows(dailyRows);
        setMonthlyDataSource('Performance API (daily → monthly)');

        if (!dailyRows.length) {
          setDailyPerfError(`No daily rows returned for ${ANALYSIS_YEAR}`);
          setMonthlyDataError(`No daily rows returned for ${ANALYSIS_YEAR}`);
        }
      } catch (e) {
        const msg = e?.message || 'Failed to load daily performance';
        setDailyPerfError(msg);
        setMonthlyDataError(msg);
      } finally {
        setDailyPerfLoading(false);
        setMonthlyDataLoading(false);
        setMonthlyDataSource((s) => (s && s !== 'Loading…') ? s : 'Daily → Monthly');
      }
    };

    fetchDailyDataset();
  }, [ANALYSIS_YEAR]);

  // Monthly derived from daily dataset
  useEffect(() => {
    if (!Array.isArray(dailyPerfRows)) return;
    const monthAcc = Array.from({ length: 12 }, () => ({ spend: 0, revenue: 0, orders: 0 }));
    for (const r of dailyPerfRows) {
      const dayStr = String(r.day || '').slice(0, 10);
      // Parse as UTC to avoid timezone drift affecting month/day
      const d = new Date(`${dayStr}T00:00:00Z`);
      if (Number.isNaN(d.getTime())) continue;
      if (d.getFullYear() !== ANALYSIS_YEAR) continue;
      const m = d.getMonth();
      monthAcc[m].spend += Number(r.spend || 0);
      monthAcc[m].revenue += Number(r.revenue || 0);
      monthAcc[m].orders += Number(r.orders || 0);
    }
    const ids = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    setMonthlyData(ids.map((id, idx) => {
      const spend = monthAcc[idx].spend;
      const revenue = monthAcc[idx].revenue;
      const orders = monthAcc[idx].orders;
      return {
        id,
        name: names[idx],
        shortName: names[idx],
        metrics: {
          spend,
          revenue,
          orders,
          roas: spend > 0 ? revenue / spend : 0,
          topProduct: '-'
        }
      };
    }));
  }, [dailyPerfRows, ANALYSIS_YEAR]);

  const calculateLeftPosition = (dateStr) => {
    if (!dateStr) return 0;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 0;

    const startOfYear = new Date(`${ANALYSIS_YEAR}-01-01T00:00:00`).getTime();
    const endOfYear = new Date(`${ANALYSIS_YEAR}-12-31T23:59:59`).getTime();
    const totalDuration = endOfYear - startOfYear;
    
    const targetDate = new Date(date);
    targetDate.setFullYear(ANALYSIS_YEAR);
    
    const currentDuration = targetDate.getTime() - startOfYear;
    let percentage = (currentDuration / totalDuration) * 100;
    return Math.max(0, Math.min(100, percentage));
  };

  const calculateWidth = (startStr, endStr) => {
    if (!startStr || !endStr) return 0;
    const start = new Date(startStr);
    const end = new Date(endStr);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 2;

    const startOfYear = new Date(`${ANALYSIS_YEAR}-01-01T00:00:00`).getTime();
    const endOfYear = new Date(`${ANALYSIS_YEAR}-12-31T23:59:59`).getTime();
    const totalDuration = endOfYear - startOfYear;
    
    const targetStart = new Date(start);
    targetStart.setFullYear(ANALYSIS_YEAR);
    const targetEnd = new Date(end);
    targetEnd.setFullYear(ANALYSIS_YEAR);
    
    let duration = targetEnd.getTime() - targetStart.getTime();
    if (duration < 0) duration = 86400000;
    
    let widthPercentage = (duration / totalDuration) * 100;
    return Math.max(widthPercentage, 1.5);
  };

  const processedPeriods = useMemo(() => {
    const sorted = [...periods].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    const lanes = [];

    return sorted.map(period => {
      const start = new Date(period.startDate);
      start.setFullYear(ANALYSIS_YEAR);
      const end = new Date(period.endDate);
      end.setFullYear(ANALYSIS_YEAR);
      
      const startTime = start.getTime();
      const endTime = end.getTime();

      let laneIndex = -1;

      for (let i = 0; i < lanes.length; i++) {
        if (lanes[i] < startTime) {
          laneIndex = i;
          break;
        }
      }

      if (laneIndex === -1) {
        laneIndex = lanes.length;
        lanes.push(endTime);
      } else {
        lanes[laneIndex] = endTime;
      }

      return {
        ...period,
        lane: laneIndex,
        // DÜZELTME: Başlangıç yüksekliğini 60px'e çıkardım (Daha fazla boşluk)
        // Ek dönem eklendikçe altta kırpılmasın diye satır aralığı + başlangıç biraz artırıldı
        styleTop: `${laneIndex * 38 + 70}px` 
      };
    });
  }, [periods]);

  // Period metrics derived from daily dataset (based on each period's selected date range)
  const computedPeriods = useMemo(() => {
    const rows = Array.isArray(dailyPerfRows) ? dailyPerfRows : [];
    if (!rows.length) return processedPeriods;

    // day -> values
    const byDay = new Map();
    for (const r of rows) {
      const day = String(r.day || '').slice(0, 10);
      if (!day) continue;
      byDay.set(day, {
        spend: Number(r.spend || 0),
        revenue: Number(r.revenue || 0),
        orders: Number(r.orders || 0),
      });
    }

    // IMPORTANT: Use UTC parsing/stepping to avoid timezone shifting (which can break filtering)
    const ymdToUtcMs = (ymd) => {
      const ms = Date.parse(`${ymd}T00:00:00Z`);
      return Number.isFinite(ms) ? ms : NaN;
    };

    return processedPeriods.map((p) => {
      const s = String(p.startDate || '').slice(0, 10);
      const e = String(p.endDate || '').slice(0, 10);
      if (!s || !e) return p;

      const startMs = ymdToUtcMs(s);
      const endMs = ymdToUtcMs(e);
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return p;
      const dayMs = 24 * 60 * 60 * 1000;

      let spend = 0;
      let revenue = 0;
      let orders = 0;
      let iter = 0;

      for (let ms = startMs; ms <= endMs && iter < 4000; ms += dayMs, iter += 1) {
        const dayKey = new Date(ms).toISOString().slice(0, 10);
        const v = byDay.get(dayKey);
        if (v) {
          spend += v.spend;
          revenue += v.revenue;
          orders += v.orders;
        }
      }

      const roas = spend > 0 ? revenue / spend : 0;
      return {
        ...p,
        metrics: {
          spend,
          revenue,
          orders,
          roas,
          topProduct: '-', // next step: compute from product-level daily
        },
      };
    });
  }, [processedPeriods, dailyPerfRows]);

  // Yükseklik hesabında da başlangıç değerini artırdım (+80px)
  // Ek lane'ler geldiğinde alt tarafta boşluk kalsın diye base + padding artırıldı
  const timelineHeight = Math.max(190, (Math.max(0, ...processedPeriods.map(p => p.lane)) + 1) * 38 + 110);

  // Money formatting: no "k" abbreviations, no "$" (use TRY by default)
  const CURRENCY = 'TRY';
  const formatCurrency = (val) => {
    const n = Number(val || 0);
    try {
      // Prefer ₺ formatting; keep 0 decimals for compact tables
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: CURRENCY,
        maximumFractionDigits: 0,
      }).format(n);
    } catch {
      // Fallback
      return `₺${Math.round(n).toLocaleString('tr-TR')}`;
    }
  };

  const formatRoas = (val) => {
    const n = Number(val || 0);
    if (!Number.isFinite(n) || n <= 0) return '0x';
    // Keep it readable; 2 decimals is enough for dashboard
    return `${n.toFixed(2)}x`;
  };

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const handleAddPeriod = () => {
    const save = async () => {
      if (!newPeriod.name || !newPeriod.startDate || !newPeriod.endDate) {
        alert('Please fill all fields');
        return;
      }
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) {
          alert('No brand selected');
          return;
        }
        const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
        const res = await fetch(`${base}/api/campaign-periods`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          body: JSON.stringify({
            name: newPeriod.name,
            startDate: newPeriod.startDate,
            endDate: newPeriod.endDate,
            color: newPeriod.color,
            description: null
          })
        });
        const js = await res.json().catch(() => ({}));
        if (!res.ok || !js?.ok || !js?.row) {
          throw new Error(js?.error || 'Failed to save period');
        }
        const r = js.row;
        const startDate = String(r.start_date || '').slice(0, 10);
        const endDate = String(r.end_date || '').slice(0, 10);
        const color = r.color || newPeriod.color;
        const opt = colorOptions.find(o => o.bg === color);
        const periodToAdd = {
          id: r.id,
          name: r.name,
          shortName: String(r.name || '').substring(0, 8),
          startDate,
          endDate,
          color,
          lightColor: opt?.light || newPeriod.lightColor,
          textColor: opt?.text || newPeriod.textColor,
          metrics: { spend: 0, revenue: 0, roas: 0, orders: 0, topProduct: '-' }
        };
        setPeriods(prev => [...prev, periodToAdd]);
        setIsModalOpen(false);
        setNewPeriod({
          name: '',
          startDate: `${ANALYSIS_YEAR}-01-01`,
          endDate: `${ANALYSIS_YEAR}-01-07`,
          color: 'bg-indigo-500',
          lightColor: 'bg-indigo-50',
          textColor: 'text-indigo-600'
        });
      } catch (e) {
        alert(e?.message || 'Failed to save period');
      }
    };
    save();
  };

  const openDeleteModal = (period) => {
    if (!period?.id) return;
    setDeleteTarget({
      id: period.id,
      name: period.name,
      startDate: String(period.startDate || '').slice(0, 10),
      endDate: String(period.endDate || '').slice(0, 10),
    });
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setDeleteTarget(null);
  };

  const handleDeletePeriod = async () => {
    if (!deleteTarget?.id) return;
    try {
      setIsDeleting(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) throw new Error('No brand selected');
      const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
      const res = await fetch(`${base}/api/campaign-periods/${deleteTarget.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok || !js?.ok) throw new Error(js?.error || 'Failed to delete period');
      setPeriods((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      alert(e?.message || 'Failed to delete period');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="p-4 bg-gray-50/50 min-h-screen font-sans text-xs">
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="flex items-center space-x-1 text-xs text-gray-500 font-medium mb-1">
            <BarChart2 size={12} />
            <span>Analysis</span>
            <ChevronRight size={10} />
            <span className="text-gray-900">Campaign Periods</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">Campaign Periods</h1>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="flex items-center space-x-1 bg-gray-900 hover:bg-black text-white px-3 py-2 rounded-lg transition-all shadow-sm text-xs font-medium"
        >
          <Plus size={14} />
          <span>New Period</span>
        </button>
      </div>

      <div 
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6 relative transition-all duration-300"
        style={{ height: `${timelineHeight}px`, minHeight: '190px' }}
      >
        <div className="absolute top-0 left-0 right-0 h-10 border-b border-gray-100 flex items-center px-4 bg-white z-20 rounded-t-xl">
           <Calendar className="mr-1.5 text-indigo-600" size={14} />
           <span className="text-sm font-bold text-gray-800">{ANALYSIS_YEAR} Calendar</span>
        </div>

        <div className="absolute top-10 left-4 right-4 bottom-6">
           <div className="absolute inset-0 flex w-full h-full z-0">
            {months.map((month, index) => (
              <div key={index} className="flex-1 border-r border-dashed border-gray-200 relative h-full">
                {/* Ay isimlerini de yukarı taşıdım (top-2) */}
                <span className="absolute top-2 left-1 text-[10px] font-semibold text-gray-400 bg-white/80 px-1 rounded z-10">
                  {month}
                </span>
              </div>
            ))}
           </div>

           <div className="absolute inset-0 w-full z-10">
            {computedPeriods.map((period) => (
              <div
                key={period.id}
                className={`absolute h-6 rounded-md shadow-sm flex items-center justify-center cursor-pointer transition-all duration-300 group
                  ${period.color} 
                  ${hoveredPeriod === period.id ? 'ring-2 ring-offset-1 ring-indigo-100 z-30 scale-[1.02]' : 'hover:opacity-90'}
                `}
                style={{
                  left: `${calculateLeftPosition(period.startDate)}%`,
                  width: `${calculateWidth(period.startDate, period.endDate)}%`,
                  top: period.styleTop // 60px, 96px...
                }}
                onMouseEnter={() => setHoveredPeriod(period.id)}
                onMouseLeave={() => setHoveredPeriod(null)}
                onClick={(e) => {
                  e.stopPropagation();
                  openDeleteModal(period);
                }}
              >
                <span className="text-white text-[10px] font-bold truncate px-1 block w-full text-center">
                  {period.shortName}
                </span>

                <div className={`absolute bottom-full mb-1 left-1/2 -translate-x-1/2 w-40 bg-gray-900 text-white text-[10px] rounded py-1.5 px-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-50`}>
                  <div className="font-bold mb-0.5">{period.name}</div>
                  <div className="text-gray-300 mb-1">{period.startDate} - {period.endDate}</div>
                  <div className="flex justify-between border-t border-gray-700 pt-1">
                    <span>ROAS:</span>
                    <span className="font-bold text-green-400">{formatRoas(period.metrics.roas)}</span>
                  </div>
                </div>
              </div>
            ))}
           </div>

           {computedPeriods.length === 0 && (
             <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400 z-0">
               {periodsLoading ? 'Loading periods…' : (periodsError ? `Error: ${periodsError}` : (<>
                 No periods yet — click <span className="mx-1 font-semibold text-gray-600">New Period</span> to add one.
               </>))}
             </div>
           )}
        </div>
      </div>

      {/* Comparison Matrix - Same as before */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
        <div className="p-3 border-b border-gray-100 bg-gray-50/30 flex justify-between items-center">
          <h2 className="text-sm font-bold text-gray-800 flex items-center">
            <TrendingUp className="mr-1.5 text-indigo-600" size={14} />
            Period Comparison
          </h2>
        </div>
        <div className="overflow-x-auto custom-scrollbar">
          {computedPeriods.length === 0 ? (
            <div className="p-6 text-xs text-gray-500">
              No periods to compare yet. Click <span className="font-semibold text-gray-700">New Period</span> and select a date range.
            </div>
          ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="py-2 px-3 text-left w-32 min-w-[120px] sticky left-0 bg-white/95 backdrop-blur z-20 border-r border-gray-100 shadow-[2px_0_12px_-6px_rgba(0,0,0,0.05)]">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">KPI & Metrics</span>
                </th>
                {computedPeriods.map(period => (
                  <th key={period.id} className={`py-2 px-3 text-left min-w-[120px] transition-colors ${hoveredPeriod === period.id ? 'bg-indigo-50/30' : ''}`}>
                    <div className="flex items-center space-x-2">
                      <div className={`w-6 h-6 rounded-md flex items-center justify-center shadow-sm ${period.color} text-white`}>
                         <span className="text-[10px] font-bold">{period.shortName.substring(0,1)}</span>
                      </div>
                      <div>
                        <div className="text-xs font-bold text-gray-900 truncate max-w-[100px]">{period.name}</div>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              <tr className="group hover:bg-gray-50/50 transition-colors">
                <td className="py-2 px-3 sticky left-0 bg-white group-hover:bg-gray-50/50 transition-colors z-10 border-r border-gray-100">
                  <div className="flex items-center">
                    <div className="p-1 rounded bg-red-50 text-red-600 mr-2"><DollarSign size={12} /></div>
                    <div><div className="text-xs font-bold text-gray-700">Total Spend</div></div>
                  </div>
                </td>
                {computedPeriods.map(period => (
                  <td key={period.id} className={`py-2 px-3 ${hoveredPeriod === period.id ? 'bg-indigo-50/20' : ''}`}>
                    <div className="text-xs font-bold text-gray-900">{formatCurrency(period.metrics.spend)}</div>
                    <div className="w-full bg-gray-100 rounded-full h-1 mt-1 overflow-hidden">
                      <div
                        className={`h-full rounded-full opacity-60 ${period.color}`}
                        style={{ width: `${Math.min(100, (Number(period.metrics.spend || 0) / 40000) * 100)}%` }}
                      ></div>
                    </div>
                  </td>
                ))}
              </tr>
              <tr className="group hover:bg-gray-50/50 transition-colors">
                <td className="py-2 px-3 sticky left-0 bg-white group-hover:bg-gray-50/50 transition-colors z-10 border-r border-gray-100">
                  <div className="flex items-center">
                    <div className="p-1 rounded bg-green-50 text-green-600 mr-2"><TrendingUp size={12} /></div>
                    <div><div className="text-xs font-bold text-gray-700">Total Revenue</div></div>
                  </div>
                </td>
                {computedPeriods.map(period => (
                  <td key={period.id} className={`py-2 px-3 ${hoveredPeriod === period.id ? 'bg-indigo-50/20' : ''}`}>
                    <div className="flex items-center">
                      <span className="text-xs font-bold text-gray-900">{formatCurrency(period.metrics.revenue)}</span>
                      <span className="ml-1 text-[10px] font-medium text-green-600 bg-green-100 px-1 py-0 rounded flex items-center"><ArrowUpRight size={8} className="mr-0.5" />12%</span>
                    </div>
                  </td>
                ))}
              </tr>
              <tr className="group bg-indigo-50/30 hover:bg-indigo-50/60 transition-colors">
                <td className="py-2 px-3 sticky left-0 bg-white group-hover:bg-indigo-50/30 transition-colors z-10 border-r border-gray-100">
                  <div className="flex items-center">
                    <div className="p-1 rounded bg-indigo-100 text-indigo-600 mr-2"><Award size={12} /></div>
                    <div><div className="text-xs font-bold text-indigo-900">ROAS</div></div>
                  </div>
                </td>
                {computedPeriods.map(period => (
                  <td key={period.id} className={`py-2 px-3 ${hoveredPeriod === period.id ? 'bg-indigo-100/40' : ''}`}>
                    <div className="text-sm font-black text-indigo-600">{formatRoas(period.metrics.roas)}</div>
                  </td>
                ))}
              </tr>
              <tr className="group hover:bg-gray-50/50 transition-colors">
                <td className="py-2 px-3 sticky left-0 bg-white group-hover:bg-gray-50/50 transition-colors z-10 border-r border-gray-100">
                  <div className="flex items-center">
                    <div className="p-1 rounded bg-orange-50 text-orange-600 mr-2"><ShoppingBag size={12} /></div>
                    <div><div className="text-xs font-bold text-gray-700">Total Orders</div></div>
                  </div>
                </td>
                {computedPeriods.map(period => (
                  <td key={period.id} className={`py-2 px-3 ${hoveredPeriod === period.id ? 'bg-indigo-50/20' : ''}`}>
                    <div className="text-xs font-semibold text-gray-700">{period.metrics.orders}</div>
                  </td>
                ))}
              </tr>
              <tr className="group hover:bg-gray-50/50 transition-colors">
                <td className="py-2 px-3 sticky left-0 bg-white group-hover:bg-gray-50/50 transition-colors z-10 border-r border-gray-100">
                  <div className="flex items-center">
                    <div className="p-1 rounded bg-gray-100 text-gray-600 mr-2"><Award size={12} /></div>
                    <div><div className="text-xs font-bold text-gray-700">Top Product</div></div>
                  </div>
                </td>
                {computedPeriods.map(period => (
                  <td key={period.id} className={`py-2 px-3 ${hoveredPeriod === period.id ? 'bg-indigo-50/20' : ''}`}>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${period.lightColor} ${period.textColor} truncate max-w-[100px]`}>{period.metrics.topProduct}</span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          )}
        </div>
      </div>

      {/* Monthly Matrix - Same */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* ... Monthly Matrix Code ... */}
        <div className="p-3 border-b border-gray-100 bg-gray-50/30 flex justify-between items-center">
          <h2 className="text-sm font-bold text-gray-800 flex items-center">
            <CalendarDays className="mr-1.5 text-indigo-600" size={14} />
            Monthly Performance ({ANALYSIS_YEAR})
          </h2>
          <div className="text-[10px] text-gray-500">
            {monthlyDataLoading ? 'Loading…' : (monthlyDataError ? `Error: ${monthlyDataError}` : monthlyDataSource)}
          </div>
        </div>
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="py-2 px-3 text-left w-32 min-w-[120px] sticky left-0 bg-white/95 backdrop-blur z-20 border-r border-gray-100 shadow-[2px_0_12px_-6px_rgba(0,0,0,0.05)]">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">KPI & Metrics</span>
                </th>
                {monthlyData.map(month => (
                  <th key={month.id} className={`py-2 px-3 text-left min-w-[80px] transition-colors ${hoveredMonth === month.id ? 'bg-indigo-50/30' : ''}`} onMouseEnter={() => setHoveredMonth(month.id)} onMouseLeave={() => setHoveredMonth(null)}>
                    <div className="flex flex-col items-start justify-center"><div className="text-xs font-bold text-gray-900">{month.name}</div></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
               {/* Satırlar aynı */}
               <tr className="group hover:bg-gray-50/50 transition-colors">
                <td className="py-2 px-3 sticky left-0 bg-white group-hover:bg-gray-50/50 transition-colors z-10 border-r border-gray-100">
                  <div className="flex items-center">
                    <div className="p-1 rounded bg-red-50 text-red-600 mr-2"><DollarSign size={12} /></div>
                    <div><div className="text-xs font-bold text-gray-700">Total Spend</div></div>
                  </div>
                </td>
                {monthlyData.map(month => (
                  <td key={month.id} className={`py-2 px-3 ${hoveredMonth === month.id ? 'bg-indigo-50/20' : ''}`} onMouseEnter={() => setHoveredMonth(month.id)} onMouseLeave={() => setHoveredMonth(null)}>
                    <div className="text-xs font-bold text-gray-900">{formatCurrency(month.metrics.spend)}</div>
                    <div className="w-full bg-gray-100 rounded-full h-1 mt-1 overflow-hidden">
                      <div
                        className={`h-full rounded-full opacity-60 bg-gray-500`}
                        style={{ width: `${Math.min(100, (Number(month.metrics.spend || 0) / 50000) * 100)}%` }}
                      ></div>
                    </div>
                  </td>
                ))}
              </tr>
              <tr className="group hover:bg-gray-50/50 transition-colors">
                <td className="py-2 px-3 sticky left-0 bg-white group-hover:bg-gray-50/50 transition-colors z-10 border-r border-gray-100">
                  <div className="flex items-center">
                    <div className="p-1 rounded bg-green-50 text-green-600 mr-2"><TrendingUp size={12} /></div>
                    <div><div className="text-xs font-bold text-gray-700">Total Revenue</div></div>
                  </div>
                </td>
                {monthlyData.map(month => (
                  <td key={month.id} className={`py-2 px-3 ${hoveredMonth === month.id ? 'bg-indigo-50/20' : ''}`} onMouseEnter={() => setHoveredMonth(month.id)} onMouseLeave={() => setHoveredMonth(null)}>
                    <div className="flex items-center"><span className="text-xs font-bold text-gray-900">{formatCurrency(month.metrics.revenue)}</span></div>
                  </td>
                ))}
              </tr>
              <tr className="group bg-indigo-50/30 hover:bg-indigo-50/60 transition-colors">
                <td className="py-2 px-3 sticky left-0 bg-white group-hover:bg-indigo-50/30 transition-colors z-10 border-r border-gray-100">
                  <div className="flex items-center">
                    <div className="p-1 rounded bg-indigo-100 text-indigo-600 mr-2"><Award size={12} /></div>
                    <div><div className="text-xs font-bold text-indigo-900">ROAS</div></div>
                  </div>
                </td>
                {monthlyData.map(month => (
                  <td key={month.id} className={`py-2 px-3 ${hoveredMonth === month.id ? 'bg-indigo-100/40' : ''}`} onMouseEnter={() => setHoveredMonth(month.id)} onMouseLeave={() => setHoveredMonth(null)}>
                    <div className="text-sm font-black text-indigo-600">{formatRoas(month.metrics.roas)}</div>
                  </td>
                ))}
              </tr>
              <tr className="group hover:bg-gray-50/50 transition-colors">
                <td className="py-2 px-3 sticky left-0 bg-white group-hover:bg-gray-50/50 transition-colors z-10 border-r border-gray-100">
                  <div className="flex items-center">
                    <div className="p-1 rounded bg-orange-50 text-orange-600 mr-2"><ShoppingBag size={12} /></div>
                    <div><div className="text-xs font-bold text-gray-700">Total Orders</div></div>
                  </div>
                </td>
                {monthlyData.map(month => (
                  <td key={month.id} className={`py-2 px-3 ${hoveredMonth === month.id ? 'bg-indigo-100/40' : ''}`} onMouseEnter={() => setHoveredMonth(month.id)} onMouseLeave={() => setHoveredMonth(null)}>
                    <div className="text-xs font-semibold text-gray-700">{month.metrics.orders}</div>
                  </td>
                ))}
              </tr>
              <tr className="group hover:bg-gray-50/50 transition-colors">
                <td className="py-2 px-3 sticky left-0 bg-white group-hover:bg-gray-50/50 transition-colors z-10 border-r border-gray-100">
                  <div className="flex items-center">
                    <div className="p-1 rounded bg-gray-100 text-gray-600 mr-2"><Award size={12} /></div>
                    <div><div className="text-xs font-bold text-gray-700">Top Product</div></div>
                  </div>
                </td>
                {monthlyData.map(month => (
                  <td key={month.id} className={`py-2 px-3 ${hoveredMonth === month.id ? 'bg-indigo-100/40' : ''}`} onMouseEnter={() => setHoveredMonth(month.id)} onMouseLeave={() => setHoveredMonth(null)}>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-800 truncate max-w-[80px]`}>{month.metrics.topProduct}</span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal - Same as before */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 transform transition-all scale-100">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-900">Add New Period</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Period Name</label>
                <input type="text" value={newPeriod.name} onChange={(e) => setNewPeriod({...newPeriod, name: e.target.value})} placeholder="e.g., Summer Sale" className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-shadow outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Start Date</label>
                  <input type="date" value={newPeriod.startDate} onChange={(e) => setNewPeriod({...newPeriod, startDate: e.target.value})} className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">End Date</label>
                  <input type="date" value={newPeriod.endDate} onChange={(e) => setNewPeriod({...newPeriod, endDate: e.target.value})} className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-2">Color Label</label>
                <div className="flex space-x-2">
                  {colorOptions.map((option) => (
                    <button key={option.name} onClick={() => setNewPeriod({ ...newPeriod, color: option.bg, lightColor: option.light, textColor: option.text })} className={`w-8 h-8 rounded-full ${option.bg} flex items-center justify-center transition-transform hover:scale-110 ${newPeriod.color === option.bg ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''}`} title={option.name}>
                      {newPeriod.color === option.bg && <Check size={14} className="text-white" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-8 flex justify-end space-x-3">
              <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors">Cancel</button>
              <button onClick={handleAddPeriod} className="px-6 py-2 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5">Save Period</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Period Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 transform transition-all scale-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Delete Period</h3>
              <button
                onClick={closeDeleteModal}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                disabled={isDeleting}
              >
                <X size={20} />
              </button>
            </div>

            <div className="text-sm text-gray-700">
              Are you sure you want to delete <span className="font-semibold">{deleteTarget.name}</span>?
              <div className="mt-2 text-xs text-gray-500">
                {deleteTarget.startDate} → {deleteTarget.endDate}
              </div>
            </div>

            <div className="mt-8 flex justify-end space-x-3">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePeriod}
                className="px-6 py-2 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 disabled:opacity-60 disabled:transform-none inline-flex items-center gap-2"
                disabled={isDeleting}
              >
                <Trash2 size={16} />
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default PeriodAnalysis;
